import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, ChevronDown, Eye, Mail, MoreVertical, Pin, PinOff, Plus, Settings2, Share2, Star, Trash2, Link2, Video, X } from 'lucide-react';
import { urlEnMeet } from '../../lib/dominios';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { COLORES, CORREO_RE } from '../../lib/calendario';
import CampoPersonas from './CampoPersonas';
import MiniMes from './MiniMes';

/** Barra lateral del calendario: crear, mes pequeño, mis calendarios, otros calendarios y páginas de reserva. */
export default function BarraLateral({
  cursor, hoy, onElegirDia, calendarios, resaltado, onCrear, onAlternar, onSoloEste, fijado, onFijar, onColor, onConfigurar,
  onQuitar, onNuevoCalendario, onAgregar, vista, onCitas, onCrearMeet, predeterminado, onPrincipal,
}) {
  // El principal (donde caen los eventos nuevos) va primero en su sección
  const primero = (a, b) => (b.id === predeterminado) - (a.id === predeterminado);
  const mios = calendarios.filter((c) => c.es_mio).sort(primero);
  const otros = calendarios.filter((c) => !c.es_mio).sort(primero);
  const acciones = { onAlternar, onSoloEste, onColor, onConfigurar, onQuitar, onFijar, onPrincipal };
  const [abiertos, setAbiertos] = useState({ mios: true, otros: true });
  const [agregar, setAgregar] = useState(false);
  const nav = useNavigate();

  return (
    <aside className="cal-lateral od-scroll" aria-label="Calendarios" data-testid="cal-lateral">
      {/* «Crear» es el botón flotante de abajo (CalendarPage): aquí casi no se veía */}
      <MiniMes valor={cursor} hoy={hoy} onElegir={onElegirDia} />

      <button type="button" className={`cal-lateral-fila cal-lateral-citas ${vista === 'citas' ? 'is-activa' : ''}`} onClick={onCitas} data-testid="cal-ir-citas">
        <CalendarCheck className="h-4 w-4" /> Páginas de reserva
      </button>
      {/* Orvian Meet: programar un evento que ya nace con su videollamada, o abrir Meet (otra pestaña) */}
      {onCrearMeet && (
        <button type="button" className="cal-lateral-fila cal-lateral-citas" onClick={onCrearMeet} data-testid="cal-crear-meet">
          <Video className="h-4 w-4" /> Programar videollamada
        </button>
      )}
      <button type="button" className="cal-lateral-fila cal-lateral-citas" onClick={() => window.open(urlEnMeet('/'), '_blank', 'noopener')} data-testid="cal-ir-meet" title="Orvian Meet (se abre en otra pestaña)">
        <Video className="h-4 w-4" /> Orvian Meet
      </button>
      <button type="button" className="cal-lateral-fila cal-lateral-citas" onClick={() => nav('/mail')} data-testid="cal-ir-correo">
        <Mail className="h-4 w-4" /> Correo
      </button>

      <Seccion titulo="Mis calendarios" abierto={abiertos.mios} onAlternar={() => setAbiertos((a) => ({ ...a, mios: !a.mios }))} accion={
        <button type="button" className="cal-icono-btn" onClick={onNuevoCalendario} aria-label="Crear calendario" title="Crear calendario" data-testid="cal-nuevo-calendario"><Plus className="h-4 w-4" /></button>
      }
      >
        {mios.map((c) => <FilaCalendario key={c.id} c={c} resaltado={resaltado === c.id} {...acciones} fijado={fijado === c.id} esPrincipal={predeterminado === c.id} />)}
      </Seccion>

      <Seccion titulo="Otros calendarios" abierto={abiertos.otros} onAlternar={() => setAbiertos((a) => ({ ...a, otros: !a.otros }))} accion={
        <button type="button" className="cal-icono-btn" onClick={() => setAgregar(true)} aria-label="Agregar el calendario de alguien" title="Agregar el calendario de alguien" data-testid="cal-agregar-otro"><Plus className="h-4 w-4" /></button>
      }
      >
        {otros.length === 0 && <p className="cal-ayuda px-3 py-1">Agrega el calendario de alguien de tu organización para ver cuándo está libre.</p>}
        {otros.map((c) => <FilaCalendario key={c.id} c={c} resaltado={resaltado === c.id} {...acciones} fijado={fijado === c.id} esPrincipal={predeterminado === c.id} />)}
      </Seccion>

      <DialogoAgregar abierto={agregar} onCerrar={() => setAgregar(false)} onAgregar={async (correo) => { const ok = await onAgregar(correo); if (ok) setAgregar(false); }} />
    </aside>
  );
}

function Seccion({ titulo, abierto, onAlternar, accion, children }) {
  return (
    <section className="cal-lateral-seccion">
      <div className="cal-lateral-cab">
        <button type="button" className="cal-lateral-titulo" onClick={onAlternar} aria-expanded={abierto}>
          {titulo}
          <ChevronDown className="h-4 w-4" style={{ transform: abierto ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
        </button>
        {accion}
      </div>
      {abierto && <div className="flex flex-col">{children}</div>}
    </section>
  );
}

function FilaCalendario({ c, resaltado, fijado, esPrincipal, onAlternar, onSoloEste, onFijar, onPrincipal, onColor, onConfigurar, onQuitar }) {
  const admin = c.mi_rol === 'admin' && c.es_mio;
  const editable = c.mi_rol === 'editor' || c.mi_rol === 'admin';
  return (
    <div className={`cal-lateral-fila cal-calendario ${resaltado ? 'is-resaltado' : ''}`} data-testid="cal-calendario">
      <label className="cal-calendario-check" title={c.dueno ? `${c.nombre} · ${c.dueno.correo}` : c.nombre}>
        <input type="checkbox" checked={!c.oculto} onChange={() => onAlternar(c)} aria-label={`Mostrar ${c.nombre}`} />
        <span className="cal-casilla" style={{ '--c': c.color }} aria-hidden="true" />
        <span className="truncate">{c.nombre}</span>
        {esPrincipal && (
          <span className="cal-estrella" title="Principal: los eventos nuevos se guardan aquí" data-testid="cal-principal-marca">
            <Star className="h-3 w-3" aria-label="Calendario principal" />
          </span>
        )}
        {fijado && <Pin className="h-3 w-3 shrink-0" style={{ color: 'var(--accent)' }} aria-label="Fijado al abrir" />}
      </label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="cal-icono-btn cal-calendario-menu" aria-label={`Opciones de ${c.nombre}`} data-testid="cal-calendario-menu"><MoreVertical className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          {/* Principal (2026-10-08): donde caen los eventos nuevos; vale uno compartido si puedo hacer cambios en él */}
          {esPrincipal ? (
            <DropdownMenuItem disabled className="cal-menu-doble" data-testid="cal-menu-principal">
              <Star className="h-4 w-4 mr-2 shrink-0 cal-estrella-llena" />
              <span><b>Es tu calendario principal</b><small>Los eventos nuevos se guardan aquí</small></span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled={!editable} onSelect={() => onPrincipal(c)} className="cal-menu-doble" data-testid="cal-menu-principal">
              <Star className="h-4 w-4 mr-2 shrink-0" />
              <span>Usar como principal<small>{editable ? 'Los eventos nuevos se guardarán aquí' : 'Necesitas permiso para hacer cambios en él'}</small></span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => onFijar(fijado ? null : c)} className="cal-menu-doble" data-testid="cal-menu-fijar">
            {fijado
              ? <><PinOff className="h-4 w-4 mr-2 shrink-0" /><span>Dejar de fijar al abrir<small>Al entrar verás todos tus calendarios</small></span></>
              : <><Pin className="h-4 w-4 mr-2 shrink-0" /><span>Fijar al abrir<small>Al entrar verás solo este</small></span></>}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onSoloEste(c)}><Eye className="h-4 w-4 mr-2" />Mostrar solo este</DropdownMenuItem>
          <DropdownMenuSeparator />
          {admin && <DropdownMenuItem onSelect={() => onConfigurar(c, 'general')} data-testid="cal-menu-configurar"><Settings2 className="h-4 w-4 mr-2" />Configuración</DropdownMenuItem>}
          {admin && <DropdownMenuItem onSelect={() => onConfigurar(c, 'compartir')} data-testid="cal-menu-compartir"><Share2 className="h-4 w-4 mr-2" />Compartir</DropdownMenuItem>}
          {admin && <DropdownMenuItem onSelect={() => onConfigurar(c, 'ics')}><Link2 className="h-4 w-4 mr-2" />Enlace de suscripción (.ics)</DropdownMenuItem>}
          {(!c.es_mio || !c.principal) && (
            <DropdownMenuItem onSelect={() => onQuitar(c)} style={{ color: 'var(--danger)' }} data-testid="cal-menu-quitar">
              {c.es_mio ? <><Trash2 className="h-4 w-4 mr-2" />Eliminar calendario</> : <><X className="h-4 w-4 mr-2" />Quitar de la lista</>}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[12px] font-medium" style={{ color: 'var(--text-3)' }}>Color en mi vista</DropdownMenuLabel>
          <div className="cal-colores px-2 pb-2">
            {COLORES.map((col) => (
              <button
                type="button"
                key={col.valor}
                className={`cal-color cal-color--chico ${c.color === col.valor ? 'is-sel' : ''}`}
                style={{ background: col.valor }}
                onClick={() => onColor(c, col.valor)}
                title={col.nombre}
                aria-label={col.nombre}
              />
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function DialogoAgregar({ abierto, onCerrar, onAgregar }) {
  const [correo, setCorreo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async (c = correo) => {
    if (!CORREO_RE.test(c.trim())) return;
    setEnviando(true);
    try { await onAgregar(c.trim().toLowerCase()); setCorreo(''); } finally { setEnviando(false); }
  };
  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="cal-dialogo-agregar">
        <DialogHeader>
          <DialogTitle>Agregar el calendario de alguien</DialogTitle>
          <DialogDescription>De tu organización. Verás lo que esa persona te deje ver (como mínimo, cuándo está ocupada).</DialogDescription>
        </DialogHeader>
        <CampoPersonas onElegir={(p) => { setCorreo(p.correo); enviar(p.correo); }} placeholder="Correo o nombre" autoFocus testId="cal-agregar-correo" />
        {correo && <p className="cal-ayuda">{correo}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={() => enviar()} disabled={enviando || !CORREO_RE.test(correo)}>Agregar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
